import { useCallback, useEffect, useState } from 'react';
import axios from '../../api/axios';
import { loadKomootMetadata } from '../../utils/komootPreview';
import { getErrorMessage } from './helpers';
import { HouseContent, HouseContentType } from './types';

interface UseHouseContentsItemsParams {
  houseId?: string;
  contentType: HouseContentType;
  failedFetchMessage: string;
}

export const useHouseContentsItems = ({
  houseId,
  contentType,
  failedFetchMessage,
}: UseHouseContentsItemsParams) => {
  const [items, setItems] = useState<HouseContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!houseId) {
      setItems([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/house-contents?houseId=${houseId}&type=${contentType}`);
      setItems(response.data);
    } catch (err: any) {
      setError(getErrorMessage(err, failedFetchMessage));
    } finally {
      setLoading(false);
    }
  }, [contentType, failedFetchMessage, houseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    items,
    loading,
    error,
    setError,
    refresh,
  };
};

export const useKomootPreviewImages = (
  contentType: HouseContentType,
  items: HouseContent[],
) => {
  const [komootPreviewImages, setKomootPreviewImages] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;

    if (contentType !== 'activities') {
      setKomootPreviewImages({});
      return () => {
        active = false;
      };
    }

    const komootUrls = Array.from(new Set(
      items
        .filter((item) => item.komootUrl && !item.images?.[0]?.url)
        .map((item) => item.komootUrl as string),
    ));

    if (!komootUrls.length) {
      setKomootPreviewImages({});
      return () => {
        active = false;
      };
    }

    Promise.all(
      komootUrls.map(async (komootUrl) => {
        const metadata = await loadKomootMetadata(komootUrl);

        return [komootUrl, metadata?.imageUrl || null] as const;
      }),
    ).then((entries) => {
      if (!active) {
        return;
      }

      setKomootPreviewImages(
        Object.fromEntries(entries.filter((entry): entry is readonly [string, string] => Boolean(entry[1]))),
      );
    });

    return () => {
      active = false;
    };
  }, [contentType, items]);

  return komootPreviewImages;
};